import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../../services/api/auth/auth.service";
import {
  hasLowerCaseValidator,
  hasNumberValidator,
  hasUpperCaseValidator,
  lengthBetween6And64Validator,
  passwordCriteriaTexts,
} from "../../validators";
import { SnackbarService } from "../../../services/snackbar/snackbar.service";
import { ThemeService } from "../../../services/theme/theme.service";

@Component({
  selector: "app-reset-password",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: "./reset-password.component.html",
  styleUrl: "./reset-password.component.scss",
})
export class ResetPasswordComponent {
  email: string = "";
  token: string = "";
  isValidToken = false;
  resetForm!: FormGroup;
  loading = true;
  errorMessage: string = "";
  showPassword = false;
  showConfirmedPassword = false;
  passwordCriteria = passwordCriteriaTexts;
  passwordControl: AbstractControl | null = null;
  private snackService = inject(SnackbarService);
  themeSerivice = inject(ThemeService);

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
  ) {}

  createForm() {
    this.resetForm = this.fb.group(
      {
        newPassword: [
          "",
          [
            Validators.required,
            lengthBetween6And64Validator,
            hasUpperCaseValidator,
            hasLowerCaseValidator,
            hasNumberValidator,
          ],
        ],
        confirmedPassword: ["", Validators.required],
      },
      { validators: this.confirmPasswordMatchValidator },
    );
    this.passwordControl = this.resetForm.get("newPassword");
  }

  getInputErrorMessage(controlName: string) {
    const control = this.resetForm.get(controlName);
    if (!control) return null;
    if (
      controlName === "confirmedPassword" &&
      this.resetForm.hasError("passwordMismatch")
    ) {
      if (control.touched || control.dirty) return "Passwords do not match";
      return null;
    }

    if (control.valid || (!control.dirty && !control.touched)) return null;
    if (control.errors?.["required"]) {
      return "This field is required";
    }
    if (control.errors?.["minlength"]) {
      const requiredLength = control.errors["minlength"].requiredLength;
      return `Minimum length is ${requiredLength} characters`;
    }
    if (control.errors?.["maxlength"]) {
      const requiredLength = control.errors["maxlength"].requiredLength;
      return `Maximum length is ${requiredLength} characters`;
    }
    return null;
  }

  confirmPasswordMatchValidator = (control: AbstractControl) => {
    const passwordControl = control.get("newPassword");
    const confirmedPasswordControl = control.get("confirmedPassword");
    if (!passwordControl || !confirmedPasswordControl) {
      return null; // If either control is missing, do not validate
    }
    if (passwordControl.value !== confirmedPasswordControl.value) {
      return { passwordMismatch: true };
    }
    return null;
  };

  resetPassword() {
    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }
    const { newPassword } = this.resetForm.value;

    // Submit the request to reset the password
    this.authService.resetPassword(this.token, newPassword).subscribe({
      next: (response) => {
        if (response && response.state === "update_password_done") {
          this.errorMessage = "";
          this.snackService.show({
            message: "Password resetted successfully",
            duration: 3000,
          });
        }
        this.router.navigate(["/login"]);
      },
      error: (err) => {
        this.errorMessage =
          err.message || "An error occurred. Please try again.";
      },
    });
  }

  ngOnInit() {
    // Getting the token
    this.token = this.route.snapshot.queryParamMap.get("token") || "";
    if (!this.token) {
      // No token means error page - not a valid link
      this.redirectToInvalidOrExpiredPage();
      return;
    }

    // Has Token, then verify the token
    this.authService.verifyResetToken(this.token).subscribe({
      next: (response) => {
        if (response && response.state === "reset_token_valid") {
          this.errorMessage = "";
          this.isValidToken = true;
          this.loading = false;
          this.createForm();
        } else if (
          response &&
          response.state === "reset_token_invalid_expired"
        ) {
          this.redirectToInvalidOrExpiredPage();
        }
      },
      error: (err) => {
        this.errorMessage =
          err.message || "An error occurred. Please try again.";
        this.snackService.show({
          message: this.errorMessage,
          duration: 5000,
        });
        console.error("Server Error: ", err);
        this.redirectToInvalidOrExpiredPage();
      },
    });
  }

  redirectToErrorPage() {
    this.router.navigate(["/error"]);
  }

  redirectToInvalidOrExpiredPage() {
    this.router.navigate(["/invalid-expired-token"]);
  }
}
